import {
  PlanStatusCallout,
  PlanStatusCalloutType,
} from "#webapp/src/components/plan-status-callout";
import { logger } from "@clockwise/client-commons/src/util/logger";
import { SmartHold, SmartHoldState } from "@clockwise/schema";

import { usePauseHold } from "#webapp/src/components/hooks/usePauseHold";
import { useProtectHold } from "#webapp/src/components/hooks/useProtectHold";
import { useUnpauseHold } from "#webapp/src/components/hooks/useUnpauseHold";
import { useMonetization } from "#webapp/src/hooks/useMonetization";
import { SmartHoldBadges } from "@clockwise/web-commons/src/ui/smart-hold-details";
import {
  PROTECT_ON_ERROR_LOG_MSG,
  PROTECT_ON_ERROR_MSG,
  SmartHoldStatePanel,
  UNPAUSE_ON_ERROR_LOG_MSG,
  UNPAUSE_ON_ERROR_MSG,
} from "@clockwise/web-commons/src/ui/smart-hold-state";
import {
  PAUSE_ON_ERROR_LOG_MSG,
  PAUSE_ON_ERROR_MSG,
} from "@clockwise/web-commons/src/ui/smart-hold-state/SmartHoldState";
import { TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";
import React from "react";
import toast from "react-hot-toast";
import { shouldHighlightAutoDeclineOption } from "../../ui-focus-wrappers/ui-focus.utils";

type SmartHoldStateContainerProps = {
  holdId: string;
  orgId: string;
  calendarId: string;
  holdName: SmartHold["__typename"];
  holdState: SmartHoldState["__typename"];
};

const SmartHoldStatePanelContainer = ({
  holdName,
  holdState,
  calendarId,
  holdId,
  orgId,
}: SmartHoldStateContainerProps) => {
  const { userIsOnActiveTrial, daysLeftInTrial } = useMonetization();
  const daysLeftOnTrial = userIsOnActiveTrial ? daysLeftInTrial : undefined;

  const doShowCallout = userIsOnActiveTrial && daysLeftOnTrial === 1;

  // currently only displaying monetization badge if viewing FT or meeting releif holds
  const showFTBadge = userIsOnActiveTrial && !!daysLeftOnTrial && holdName === "FocusTimeHold";
  const showMRBadge = holdName === "MeetingReliefHold";
  const badge = showFTBadge ? (
    <SmartHoldBadges.FocusTime daysLeftOnTrial={daysLeftOnTrial} isOnTrial={userIsOnActiveTrial} />
  ) : showMRBadge ? (
    <SmartHoldBadges.MeetingRelief />
  ) : null;

  const holdStateInput = { orgId, holdId };

  const [onProtectHold] = useProtectHold({
    input: { ...holdStateInput },
    calendarId,
    onError: (error) => {
      logger.error(PROTECT_ON_ERROR_LOG_MSG, error);
      toast.error(PROTECT_ON_ERROR_MSG);
    },
  });

  const [onUnpauseHold] = useUnpauseHold({
    input: { ...holdStateInput },
    calendarId,
    onError: (error) => {
      logger.error(UNPAUSE_ON_ERROR_LOG_MSG, error);
      toast.error(UNPAUSE_ON_ERROR_MSG);
    },
  });

  const [onPauseHold] = usePauseHold({
    input: { ...holdStateInput },
    calendarId,
    onError: (error) => {
      logger.error(PAUSE_ON_ERROR_LOG_MSG, error);
      toast.error(PAUSE_ON_ERROR_MSG);
    },
  });

  const getPlanStatusTrackingEvent = () => {
    if (holdName === "FocusTimeHold") {
      return TrackingEvents.TRIAL.FOCUS_TIME_SIDEBAR_VIEW_ONE_DAY_LEFT_UPGRADE_CLICKED;
    } else {
      return TrackingEvents.TRIAL.FOCUS_TIME_SIDEBAR_VIEW_ONE_DAY_LEFT_UPGRADE_CLICKED;
    }
  };

  return (
    <>
      {doShowCallout && (
        <div className="cw-mb-3">
          <PlanStatusCallout
            calloutType={PlanStatusCalloutType.TrialOneDayLeft_FocusTime}
            trackingEvent={getPlanStatusTrackingEvent()}
          />
        </div>
      )}
      <SmartHoldStatePanel
        holdId={holdId}
        holdName={holdName}
        holdState={holdState}
        badge={badge}
        onProtectHold={onProtectHold}
        onUnpauseHold={onUnpauseHold}
        onPauseHold={onPauseHold}
        surface="Sidepanel"
        highlightProtectOption={shouldHighlightAutoDeclineOption()}
      />
    </>
  );
};

export default SmartHoldStatePanelContainer;
